import { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Car, Plus, X, Pencil } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-MY", { day: "2-digit", month: "short", year: "numeric" });
}

function typeLabel(type) {
  if (!type) return "—";
  if (type === "Sticker fee - Car") return "Car";
  if (type === "Sticker fee - Motorcycle") return "Motorcycle";
  return type;
}

function selectType(type) {
  if (type === "Sticker fee - Motorcycle" || String(type || "").toLowerCase() === "motorcycle") return "Motorcycle";
  return "Car";
}

export default function VehiclePlates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plate, setPlate] = useState("");
  const [houseAlias, setHouseAlias] = useState("");
  const [debouncedPlate, setDebouncedPlate] = useState("");
  const [debouncedHouse, setDebouncedHouse] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedPlate(plate.trim());
      setDebouncedHouse(houseAlias.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [plate, houseAlias]);

  useEffect(() => {
    fetchPlates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlate, debouncedHouse]);

  async function fetchPlates() {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (debouncedPlate) params.set("plate", debouncedPlate);
      if (debouncedHouse) params.set("house_alias", debouncedHouse);
      const qs = params.toString();
      const res = await apiGet(`/vehicle-plates${qs ? `?${qs}` : ""}`);
      const body = res.data;
      setRows(Array.isArray(body) ? body : body.data ?? []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load registered vehicles.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  function clearFilters() {
    setPlate("");
    setHouseAlias("");
  }

  return (
    <div>
      <div className="section-panel" data-testid="vehicle-plates-panel">
        <div className="section-head">
          <div className="section-title">Registered Vehicles</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: "var(--text2)" }}>{rows.length} record{rows.length === 1 ? "" : "s"}</span>
            {isAdmin && (
              <button
                type="button"
                className="btn btn-primary"
                style={{ fontSize: 11, padding: "4px 10px" }}
                onClick={() => setShowAdd(true)}
                data-testid="open-add-vehicle-btn"
              >
                <Plus size={12} /> Add Vehicle
              </button>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, padding: "10px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", alignItems: "center" }}>
          <div className="head-search-box" style={{ position: "relative" }}>
            <Car size={11} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text2)" }} />
            <input
              className="form-input head-search-input"
              style={{ paddingLeft: 26, fontSize: 11, height: 28, width: 180 }}
              placeholder="e.g. WXY1234"
              value={plate}
              onChange={e => setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              data-testid="vehicle-plate-filter"
            />
          </div>
          <div className="head-search-box" style={{ position: "relative" }}>
            <Search size={11} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text2)" }} />
            <input
              className="form-input head-search-input"
              style={{ paddingLeft: 26, fontSize: 11, height: 28, width: 180 }}
              placeholder="House alias"
              value={houseAlias}
              onChange={e => setHouseAlias(e.target.value)}
              data-testid="vehicle-house-filter"
            />
          </div>
          {(plate || houseAlias) && (
            <button type="button" className="btn" style={{ fontSize: 10, padding: "3px 9px" }} onClick={clearFilters} data-testid="vehicle-clear-filters">
              Clear
            </button>
          )}
        </div>

        {error && <div className="auth-error" style={{ margin: 16 }}>{error}</div>}

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading vehicles...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="percasa-table">
              <thead>
                <tr>
                  <th>Plate Number</th>
                  <th>Type</th>
                  <th>House Alias</th>
                  <th>Registered</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} style={{ textAlign: "center", color: "var(--text2)", padding: 24 }}>
                      No registered vehicles found
                    </td>
                  </tr>
                ) : rows.map(r => (
                  <tr key={r.id}>
                    <td><span className="mono" style={{ fontWeight: 600 }}>{r.vehicle_plate_no}</span></td>
                    <td style={{ fontSize: 11 }}>{typeLabel(r.type)}</td>
                    <td><span className="mono">{r.house_alias || "—"}</span></td>
                    <td style={{ fontSize: 10, color: "var(--text2)" }}>{fmtDate(r.created_at)}</td>
                    {isAdmin && (
                      <td>
                        <button
                          type="button"
                          className="btn"
                          style={{ fontSize: 11, padding: "3px 9px" }}
                          onClick={() => setEditRow(r)}
                          data-testid={`edit-vehicle-${r.id}`}
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <AddVehicleForm
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            fetchPlates();
          }}
        />
      )}
      {editRow && (
        <EditVehicleForm
          vehicle={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => {
            setEditRow(null);
            fetchPlates();
          }}
        />
      )}
    </div>
  );
}

function AddVehicleForm({ onClose, onSaved }) {
  const [houseAlias, setHouseAlias] = useState("");
  const [plate, setPlate] = useState("");
  const [type, setType] = useState("Car");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const alias = houseAlias.trim();
    const plateNo = plate.replace(/[^A-Z0-9]/g, "");
    if (!alias) {
      setError("House alias is required.");
      return;
    }
    if (!plateNo) {
      setError("Vehicle plate number is required.");
      return;
    }
    setSaving(true);
    try {
      await apiPost("/vehicle-plates", {
        house_alias: alias,
        vehicle_plate_no: plateNo,
        type,
      });
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save vehicle.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="add-vehicle-overlay">
      <div className="modal-box" data-testid="add-vehicle-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          Add Vehicle
          <button type="button" className="btn" onClick={onClose} data-testid="close-add-vehicle-btn" aria-label="Close">
            <X size={14} />
          </button>
        </div>
        {error && <div className="auth-error" data-testid="add-vehicle-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">House Alias *</label>
            <input
              className="form-input"
              placeholder="e.g. 01-1-1"
              value={houseAlias}
              onChange={e => setHouseAlias(e.target.value)}
              required
              data-testid="add-vehicle-house-alias"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Vehicle Plate Number *</label>
            <input
              className="form-input"
              placeholder="e.g. WXY1234"
              value={plate}
              maxLength={15}
              onChange={e => setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              required
              data-testid="add-vehicle-plate"
            />
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 6 }}>
              Letters and numbers only. Spaces are not allowed.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select
              className="form-input"
              value={type}
              onChange={e => setType(e.target.value)}
              data-testid="add-vehicle-type"
            >
              <option value="Car">Car</option>
              <option value="Motorcycle">Motorcycle</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} data-testid="cancel-add-vehicle-btn">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="add-vehicle-submit-btn">
              {saving ? "Saving..." : "Save Vehicle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditVehicleForm({ vehicle, onClose, onSaved }) {
  const [plate, setPlate] = useState(String(vehicle.vehicle_plate_no || "").toUpperCase());
  const [type, setType] = useState(selectType(vehicle.type));
  const [adminPassword, setAdminPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const plateNo = plate.replace(/[^A-Z0-9]/g, "");
    if (!plateNo) {
      setError("Vehicle plate number is required.");
      return;
    }
    if (!adminPassword) {
      setError("Admin password is required to confirm this update.");
      return;
    }
    setSaving(true);
    try {
      await apiPut(`/vehicle-plates/${vehicle.id}`, {
        vehicle_plate_no: plateNo,
        type,
        admin_password: adminPassword,
      });
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to update vehicle.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="edit-vehicle-overlay">
      <div className="modal-box" data-testid="edit-vehicle-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          Edit Vehicle
          <button type="button" className="btn" onClick={onClose} data-testid="close-edit-vehicle-btn" aria-label="Close">
            <X size={14} />
          </button>
        </div>
        {error && <div className="auth-error" data-testid="edit-vehicle-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">House Alias</label>
            <input
              className="form-input"
              value={vehicle.house_alias || ""}
              readOnly
              style={{ background: "var(--bg)", color: "var(--text2)" }}
              data-testid="edit-vehicle-house-alias"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Vehicle Plate Number *</label>
            <input
              className="form-input"
              placeholder="e.g. WXY1234"
              value={plate}
              maxLength={15}
              onChange={e => setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              required
              data-testid="edit-vehicle-plate"
            />
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 6 }}>
              Letters and numbers only. Spaces are not allowed.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select
              className="form-input"
              value={type}
              onChange={e => setType(e.target.value)}
              data-testid="edit-vehicle-type"
            >
              <option value="Car">Car</option>
              <option value="Motorcycle">Motorcycle</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Your Admin Password <span style={{ color: "var(--danger)" }}>*</span></label>
            <input
              type="password"
              className="form-input"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              placeholder="Enter your admin password to confirm"
              required
              autoFocus
              data-testid="edit-vehicle-admin-password"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} data-testid="cancel-edit-vehicle-btn">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="edit-vehicle-submit-btn">
              {saving ? "Updating..." : "Update Vehicle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
